import React, { useState } from "react";
import { supabase } from "../supabaseClient";

export default function Quiz({ studentInfo, questions, onComplete }) {
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  if (
    !studentInfo?.name ||
    !studentInfo?.subject ||
    !studentInfo?.grade ||
    !studentInfo?.email
  ) {
    return (
      <p style={{ padding: 40 }}>
        Missing student info. Please restart the quiz.
      </p>
    );
  }

  const handleSelect = (index, selected) => {
    const updated = [...answers];
    updated[index] = selected;
    setAnswers(updated);
  };

  const handleSubmit = async () => {
    try {
      let sc = 0;
      const marked = questions.map((q, i) => {
        const selected = answers[i];
        const correct = q.correct === selected;
        if (correct) sc += 1;
        return { question: q.questionText, selected, correct };
      });

      setScore(sc);
      setSubmitted(true);

      const quizId = `${studentInfo.grade}_${studentInfo.subject}`.toLowerCase();

      const { error } = await supabase.from("quiz_results").insert({
        name: studentInfo.name,
        email: studentInfo.email,
        subject: studentInfo.subject,
        grade: studentInfo.grade,
        score: sc,
        total: questions.length,
        answers: marked,
        quiz_id: quizId,
      });

      if (error) {
        console.error("[Quiz] Supabase insert failed:", error.message);
      } else {
        console.log("[Quiz] Quiz results saved for", quizId);
        if (onComplete) onComplete();
      }
    } catch (err) {
      console.error("[Quiz] Error submitting quiz:", err);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoContainer}>
          <img src="/logo1.png" alt="Logo" style={styles.logo} />
        </div>

        <h2 style={styles.heading}>
          Grade {studentInfo.grade} – {studentInfo.subject?.toUpperCase()} Assessment
        </h2>

        {questions.map((q, i) => (
          <div key={i} style={styles.questionBlock}>
            {/* Insert comprehension only before Question 3 for Grade 7 English */}
            {studentInfo.subject?.toLowerCase() === "english" &&
              studentInfo.grade === "7" &&
              i === 2 && (
                <div style={styles.comprehensionBox}>
                  <h4>Comprehension Passage</h4>
                  <p>
                    Have you ever wondered what it would be like to live on another planet?
                    Imagine waking up on Mars or Venus, where the air is different and gravity
                    is weaker. Space exploration has made great advances in the last few
                    decades, and scientists are now more optimistic than ever about the
                    possibility of humans living on other planets. While the idea is still far from
                    reality, the dream of exploring new worlds continues to inspire people
                    around the globe.</p>
                    <p>
                    Many space missions have been launched, sending robots to collect
                    information and even images of distant planets. These missions help
                    scientists learn more about what we might need to survive on another
                    planet. With new technology, we could one day travel beyond our own solar
                    system and discover other planets that might support life.
                  </p>
                </div>
              )}

            <p style={styles.questionText}>
              {i + 1}. {q.questionText}
            </p>
            <div style={styles.optionsContainer}>
              {q.options.map((opt) => {
                const isSelected = answers[i] === opt;
                return (
                  <label
                    key={opt}
                    style={{
                      ...styles.optionLabel,
                      backgroundColor: isSelected ? "#f0f8ff" : "#f9f9f9",
                      borderColor: isSelected ? "#00154A" : "#E2E4E8",
                      color: "#333333",
                    }}
                  >
                    <input
                      type="radio"
                      name={`q-${i}`}
                      value={opt}
                      checked={isSelected}
                      onChange={() => handleSelect(i, opt)}
                      disabled={submitted}
                      style={styles.radio}
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        {!submitted ? (
          <button style={styles.button} onClick={handleSubmit}>
            Submit Quiz
          </button>
        ) : (
          <div style={styles.scoreBox}>
            <h3>
              You scored {score} out of {questions.length}
            </h3>
            <p style={styles.completionMessage}>
              Well done! You’ve completed the <strong>{studentInfo.subject}</strong> quiz.
            </p>
            <img
              src="/logo1.png"
              alt="Logo"
              style={{ marginTop: 20, height: 60, objectFit: "contain" }}
            />
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 40, textAlign: "center", color: "#555", fontSize: 14 }}>
          <p>Proudly supported by</p>
          <img
            src="/logo3.png"
            alt="Supporter Logo"
            style={{ height: 40, objectFit: "contain", marginTop: 8, filter: "grayscale(0.2)" }}
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(to right, #f5f7fa, #e2e4e8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "40px 20px",
    fontFamily: "Montserrat, sans-serif",
  },
  card: {
    background: "#fff",
    padding: "30px 25px",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0, 21, 74, 0.1)",
    width: "100%",
    maxWidth: 600,
    position: "relative",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "flex-start",
    marginBottom: 20,
  },
  logo: {
    height: 40,
    objectFit: "contain",
  },
  heading: {
    fontSize: 26,
    fontWeight: 700,
    marginBottom: 30,
    color: "#00154A",
    textAlign: "center",
  },
  questionBlock: {
    marginBottom: 35,
  },
  comprehensionBox: {
    backgroundColor: "#f4f6fa",
    padding: "16px",
    borderRadius: "10px",
    marginBottom: "20px",
    borderLeft: "5px solid #00154A",
  },
  questionText: {
    fontWeight: 600,
    marginBottom: 10,
    color: "#333333",
  },
  optionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  optionLabel: {
    display: "flex",
    alignItems: "center",
    padding: "12px 15px",
    border: "1px solid #E2E4E8",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 15,
    transition: "all 0.2s ease",
    userSelect: "none",
    background: "#f9f9f9",
  },
  radio: {
    marginRight: 10,
    accentColor: "#00154A",
  },
  button: {
    background: "#00154A",
    color: "#fff",
    padding: "14px 20px",
    border: "none",
    borderRadius: 10,
    fontSize: 16,
    cursor: "pointer",
    width: "100%",
    boxShadow: "0 4px 12px rgba(0, 21, 74, 0.25)",
    transition: "background 0.3s ease",
  },
  scoreBox: {
    marginTop: 20,
    textAlign: "center",
    backgroundColor: "#f0fff4",
    padding: "18px",
    borderRadius: 10,
    color: "#02A552",
    fontWeight: "bold",
  },
  completionMessage: {
    fontSize: 14,
    color: "#333",
    marginTop: 12,
  },
  personalityButton: {
    display: "inline-block",
    background: "#02A552",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: "8px",
    fontSize: 15,
    textDecoration: "none",
    fontWeight: "600",
    transition: "0.3s ease",
  },
};


















